import { formatPrice } from '@lichtblick/localization';
import { Text } from '@lichtblick/photon/atoms/text/text';
import classNames from 'classnames';
import type { FC } from 'react';
import type { ProductType } from '@/components/card/card-utils';
import styles from '@/components/electricity-tariffs/card.module.scss';
import type { TariffsQuery } from '@/types/generated';

type BenefitTextType = 'marketingClaim' | 'bonus' | 'priceGuarantee';

type BenefitsColors = 'moss' | 'berry' | 'smoke';

type BenefitsColor = Record<BenefitTextType, BenefitsColors>;

type BenefitsType = Record<
  ProductType,
  (product: TariffsQuery['tariffs'][number]) => Record<BenefitTextType, string | undefined>
>;

export const benefitsColor: BenefitsColor = {
  marketingClaim: 'berry',
  bonus: 'moss',
  priceGuarantee: 'smoke',
};

const getTotalBonus = (product: TariffsQuery['tariffs'][number]) => {
  const newCustomerBonusInEur = product.bonus?.newCustomerBonusInEur ?? 0;
  const instantBonusInEur = product.bonus?.instantBonusInEur ?? 0;
  return Math.floor(newCustomerBonusInEur + instantBonusInEur);
};

export const benefits: BenefitsType = {
  relax: (product) => {
    const totalBonusInEur = getTotalBonus(product);

    return {
      marketingClaim: totalBonusInEur > 0 ? 'maximale Sicherheit & sparen durch Bonus¹' : undefined,
      bonus: totalBonusInEur > 0 ? `${formatPrice(totalBonusInEur)} Bonus¹` : undefined,
      priceGuarantee: '24 Monate Energiepreisgarantie²',
    };
  },
  komfort: (product) => {
    const totalBonusInEur = getTotalBonus(product);

    return {
      marketingClaim: totalBonusInEur > 0 ? 'kurze Laufzeit & sparen durch Bonus¹' : undefined,
      bonus: totalBonusInEur > 0 ? `${formatPrice(totalBonusInEur)} Bonus¹` : undefined,
      priceGuarantee: '12 Monate Energiepreisgarantie²',
    };
  },
  vario: () => ({
    marketingClaim: `variabler Arbeitspreis²`,
    bonus: undefined,
    priceGuarantee: 'monatlich kündbar',
  }),
  easy12: () => ({
    marketingClaim: 'kurze Laufzeit & günstiger Arbeitspreis',
    bonus: undefined,
    priceGuarantee: '12 Monate Energiepreisgarantie²',
  }),
  easy24: () => ({
    marketingClaim: 'langfristig sicher & günstiger Arbeitspreis',
    bonus: undefined,
    priceGuarantee: '24 Monate Energiepreisgarantie²',
  }),
};

interface BenefitsRowProps {
  text: string;
  color: BenefitsColors;
  placeholder: boolean;
}

export const CardBenefitsRow: FC<BenefitsRowProps> = ({ text, color, placeholder }) => {
  return (
    <li
      className={classNames(
        styles['benefits-row'],
        styles[`benefits-row-${color}`],
        placeholder && styles['benefits-row-placeholder'],
      )}
    >
      <Text bold size="xs">
        {text}
      </Text>
    </li>
  );
};
